import { type GridColDef, GridActionsCellItem, type GridSortModel } from '@mui/x-data-grid-pro';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toTitleCase } from './HelperFunction';
import { createRenderColumnHeader } from '../components/common/GridHeaderUtils';
import { applyDashValueFormatter, formatMissingText } from './missingValue';

export function getWorkflowprotocolCols(classes: any, hasAccess: boolean = true, sortModel: GridSortModel = []): GridColDef[] {
    const renderHeader = createRenderColumnHeader(sortModel);
    const columns: GridColDef[] = [
        {
            field: 'protocol_title', 
            headerName: 'Workflow Name', 
            minWidth: 150,
            flex: 1,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => (
                <Typography className={classes.labelNoto16Bold400} sx={{ fontSize: '14px', color: '#1F2937', fontWeight: 500 }}>
                    {formatMissingText(params?.row?.protocol_title)}
                </Typography>
            ),
        },
        {
            field: 'cropName', 
            headerName: 'Crop', 
            minWidth: 120,
            flex: 0.8,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => (
                <Typography className={classes.labelNoto16Bold400} sx={{ fontSize: '14px', color: '#1F2937', fontWeight: 500 }}>
                    {formatMissingText(params?.value)}
                </Typography>
            ),
        },
        {
            field: 'siteName', 
            headerName: 'Site', 
            minWidth: 120,
            flex: 0.8,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => (
                <Typography className={classes.labelNoto16Bold400} sx={{ fontSize: '14px', color: '#1F2937', fontWeight: 500 }}>
                    {formatMissingText(params?.value)}
                </Typography>
            ),
        },
        {
            field: 'protocol_year', 
            headerName: 'Year', 
            minWidth: 100,
            flex: 0.6,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => (
                <Typography className={classes.labelNoto16Bold400} sx={{ fontSize: '14px', color: '#1F2937', fontWeight: 500 }}>
                    {formatMissingText(params?.row?.protocol_year)}
                </Typography>
            ),
        },
        {
            field: 'protocol_details', 
            headerName: 'Protocol Steps', 
            minWidth: 300,
            flex: 2,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => {
                const steps = params?.row?.protocol_steps || [];
                if (!Array.isArray(steps) || steps.length === 0) {
                    return (
                        <Typography className={classes.labelNoto16Bold400} sx={{ fontSize: '14px', color: '#1F2937', fontWeight: 500 }}>
                            -
                        </Typography>
                    );
                }
                return (
                    <Box sx={{ 
                        width: '100%', 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: 0.5,
                        py: 1,
                        alignItems: 'center'
                    }}>
                        {steps.map((step: any, index: number) => (
                            <Chip 
                                key={index}
                                label={`${index + 1}. ${step}`}
                                size="small"
                                className={classes.labelNoto16Bold500}
                                sx={{
                                    height: '24px',
                                    fontSize: '12px',
                                    backgroundColor: '#F3F4F6',
                                    color: '#374151',
                                    fontWeight: 400,
                                    borderRadius: '8px',
                                    border: 'none',
                                    '& .MuiChip-label': {
                                        px: 1.5,
                                        py: 0.5
                                    }
                                }}
                            />
                        ))}
                    </Box>
                );
            },
        },
        {
            field: 'protocol_status', 
            headerName: 'Status', 
            minWidth: 100,
            flex: 0.7,
            align: 'center',
            headerAlign: 'center',
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => {
                const status = params?.row?.protocol_status;
                if (!status) {
                    return <Typography className={classes.labelNoto16Bold400}>-</Typography>;
                }
                const isActive = status?.toLowerCase() === 'active';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Chip 
                            label={toTitleCase(status)} 
                            size="small"
                            className={classes.labelNoto16Bold500}
                            sx={{ 
                                backgroundColor: isActive ? '#85D895' : '#F7E392',
                                color: isActive ? '#0A0A0A' : '#0A0A0A',
                                fontWeight: 500,
                                fontSize: '12px',
                                height: '24px',
                                borderRadius: '12px',
                                border: 'none',
                                '& .MuiChip-label': {
                                    px: 1.5
                                }
                            }} 
                        />
                    </Box>
                );
            },
        }
    ];

    if (hasAccess) {
        columns.push({
            field: 'actions', 
            headerName: 'Action', 
            width: 120, 
            type: 'actions',
            sortable:false,
            headerClassName: classes.labelNoto14Bold700,
            getActions: () => [
                <Tooltip key="archive" title="Action not ready. Under development." arrow placement="top">
                    <span>
                        <GridActionsCellItem
                            icon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />}
                            label="Archive"
                            disabled
                        />
                    </span>
                </Tooltip>,
                <Tooltip key="edit" title="Action not ready. Under development." arrow placement="top">
                    <span>
                        <GridActionsCellItem
                            icon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                            label="Edit"
                            disabled
                        />
                    </span>
                </Tooltip>
            ]
        });
    }

    return applyDashValueFormatter(columns);
}
